import { useEffect, useState } from "react";
import { isMacOs } from "react-device-detect";
import { MAX_HOTKEY_POSITION } from "../constants";

const SHORTCUT_BASE_KEYS = ["alt", "g"] as const;

interface ShortcutKey {
  readonly source: string;
  readonly display: string;
  readonly label: string;
}

const toShortcutKey = (source: string, isMac: boolean): ShortcutKey => {
  const lower = source.toLowerCase();
  if (lower === "alt" || lower === "option") {
    return isMac
      ? { source, display: "⌥", label: "Option" }
      : { source, display: "Alt", label: "Alt" };
  }
  const upper = source.toUpperCase();
  return { source, display: upper, label: upper };
};

export interface CommunitySectionTooltipContentProps {
  readonly name: string;
  readonly hotkeyPosition?: number;
}

export const CommunitySectionTooltipContent = ({
  name,
  hotkeyPosition,
}: CommunitySectionTooltipContentProps) => {
  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    setIsMac(isMacOs);
  }, []);

  const shouldShowShortcut =
    typeof hotkeyPosition === "number" &&
    hotkeyPosition >= 0 &&
    hotkeyPosition <= MAX_HOTKEY_POSITION;

  if (!shouldShowShortcut) {
    return <>{name}</>;
  }

  const shortcutKeys = [...SHORTCUT_BASE_KEYS, String(hotkeyPosition)].map(
    source => toShortcutKey(source, isMac),
  );
  const shortcutLabel = shortcutKeys.map(({ label }) => label).join("+");

  return (
    <span className="flex items-center justify-between gap-2">
      <span>{name}</span>
      <span
        aria-label={shortcutLabel}
        className="flex items-center gap-1"
        role="img"
      >
        {shortcutKeys.map(({ source, display }) => (
          <kbd
            key={source}
            aria-hidden="true"
            className="inline-flex min-h-[16px] min-w-[16px] items-center justify-center rounded bg-white/20 px-1 text-[11px] font-medium leading-none"
          >
            {display}
          </kbd>
        ))}
      </span>
    </span>
  );
};
